import { Component } from 'react';

// Tras un nuevo despliegue, los chunks JS viejos (con hash) dejan de existir en
// el servidor. Una pestaña abierta desde antes del deploy que navega a una ruta
// no cargada todavía intenta bajar un chunk viejo → 404 → esto es justamente
// el error que tira el import() dinámico de React.lazy.
const CHUNK_ERROR_RE = /dynamically imported module|importing a module script failed|failed to fetch/i;
export const CHUNK_RELOAD_FLAG = 'msf_chunk_reload';

// Manda el crash al backend (queda en los logs de Railway) para no depender
// de que alguien copie/pegue la consola a mano. Incluye el tipo de conexión
// real (Network Information API) para poder ver si coincide con latencia alta.
function reportToServer(name, error, errorInfo) {
  try {
    const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    const payload = JSON.stringify({
      name: name || '',
      message: error?.message || String(error),
      stack: error?.stack || '',
      componentStack: errorInfo?.componentStack || '',
      url: window.location.href,
      connection: conn ? { effectiveType: conn.effectiveType, downlink: conn.downlink, rtt: conn.rtt, saveData: conn.saveData } : null,
    });
    const sent = navigator.sendBeacon?.('/api/client-error', new Blob([payload], { type: 'application/json' }));
    if (!sent) fetch('/api/client-error', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: payload, keepalive: true }).catch(() => {});
  } catch {
    /* no bloquear el manejo del error por esto */
  }
}

export default class ErrorBoundary extends Component {
  state = { error: null };

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, errorInfo) {
    // Con sourcemaps activos en el build, este stack se puede pegar en la consola
    // del navegador (o revisar en el panel de red) para ver el origen real, no minificado.
    console.error(`[ErrorBoundary${this.props.name ? ':' + this.props.name : ''}]`, error, '\nComponent stack:', errorInfo?.componentStack);
    reportToServer(this.props.name, error, errorInfo);

    if (CHUNK_ERROR_RE.test(error?.message || '') && !sessionStorage.getItem(CHUNK_RELOAD_FLAG)) {
      // Recargar una sola vez alcanza para traer el index.html y los chunks vigentes.
      sessionStorage.setItem(CHUNK_RELOAD_FLAG, '1');
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (CHUNK_ERROR_RE.test(error?.message || '')) return null; // recargando
      if (this.props.silent) return null; // pieza de UI no crítica: no tumbar toda la página

      if (this.props.compact) {
        // Se usa dentro del layout (con key={pathname}): navegar a otra URL
        // remonta este boundary desde cero, sin necesitar un reload manual.
        return (
          <div className="grid place-items-center px-6 py-32 text-center">
            <div>
              <p className="font-serif text-2xl">No se pudo cargar esta página</p>
              <p className="mt-2 text-ink/60 dark:text-mist-100/60">Probá volver a intentar o navegá a otra sección.</p>
              <button onClick={() => window.location.reload()} className="btn-primary mt-6">
                Recargar página
              </button>
            </div>
          </div>
        );
      }

      return (
        <div className="grid min-h-screen place-items-center px-6 text-center">
          <div>
            <p className="font-serif text-3xl">Algo salió mal</p>
            <p className="mt-2 text-ink/60 dark:text-mist-100/60">
              Ocurrió un error inesperado. Probá recargar la página.
            </p>
            <button onClick={() => window.location.reload()} className="btn-primary mt-6">
              Recargar página
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
